// src/components/pages/message/timecapsule/TimeCapsuleWizard.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Step1Compose from "./Step1Compose";
import Step2Recipients from "./Step2Recipients";
import Step3Review from "./Step3Review";
import Step1ComposeFrictionless from "./Step1ComposeFrictionless";
import Step2MediaFrictionless from "./Step2MediaFrictionless";
import Step3ShareFrictionless from "./Step3ShareFrictionless";
import { Box, Stepper, Step, StepLabel, Snackbar, Alert, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from "framer-motion";
import useAppConfigFlags from "../../../../hooks/useAppConfigFlags";
import usePrivateTimeCapsuleSend from "../../../../hooks/usePrivateTimeCapsuleSend";
import { useTheme } from "@mui/material/styles";
import { storage, functions as firebaseFunctions } from "../../../../firebase";
import { ref as storageRef, getDownloadURL, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../../../../firebase";
import {
  buildVideoPosterFileFromPreview,
  buildUploadableMediaFile,
  buildVideoSessionPreviewDataUrl,
  isForwardedSourceMediaItem,
  isGooglePlaceImportUrlItem,
  mapStoredMediaToWizardItem,
  shouldUploadWizardMediaItem,
} from "../../../../utils/timeCapsuleMedia";
import {
  mapForwardedMediaToWizardItem,
  shouldUseFreshTimeCapsuleWizardSession,
} from "../../../../utils/timeCapsuleForward";
import { uploadWithRetries } from "../../../../utils/resumableUpload";
import { FRIEND_CAPSULE_TITLE_PREFIX } from "../../../../utils/discoveryRowLabels";
import {
  NETWORK_OFFLINE_CODE,
  checkNetworkReachability,
} from "../../../../utils/networkReachability";

const stepsLegacy = ["Compose", "Recipients", "Review"];
const stepsShareFlow = ["Compose", "Add media & bury"];
const INITIAL_DRAFT_CREATE_MAX_ATTEMPTS = 3;
const REFRESH_RECOVERY_TTL_MS = 6 * 60 * 60 * 1000;
const VIDEO_PREVIEW_BEST_EFFORT_MS = 2500;
const MAX_SHARE_FLOW_VIDEO_SECONDS = 10;
const MAX_SHARE_FLOW_VIDEO_VALIDATION_SECONDS = 11;
const VIDEO_FILE_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "m4v",
  "webm",
  "ogg",
  "ogv",
  "3gp",
  "3gpp",
]);

function getUsableLatLng(latValue, lngValue) {
  if (typeof latValue === "string" && latValue.trim() === "") return null;
  if (typeof lngValue === "string" && lngValue.trim() === "") return null;
  if (latValue == null || lngValue == null) return null;
  const lat = Number(latValue);
  const lng = Number(lngValue);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

function getFileExtension(name = "") {
  const cleanName = String(name || "").split("?")[0].split("#")[0];
  const parts = cleanName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function fileLooksLikeVideo(file) {
  if (!(file instanceof File)) return false;
  const mime = String(file.type || "").toLowerCase();
  if (mime.startsWith("video/")) return true;
  if (mime.startsWith("image/")) return false;
  return VIDEO_FILE_EXTENSIONS.has(getFileExtension(file.name));
}

function getMediaItemDiagnostics(item) {
  const file = item?.file || null;
  return {
    clientId: item?.clientId || "",
    source: item?.source || "",
    type: item?.type || "",
    urlKind: String(item?.url || "").startsWith("blob:") ? "blob" : (item?.url ? "remote" : "none"),
    file: file instanceof File
      ? {
          name: file.name || "",
          type: file.type || "",
          size: file.size,
          lastModified: file.lastModified || 0,
        }
      : null,
  };
}

function shouldUploadBeforeBury(item) {
  return shouldUploadWizardMediaItem(item);
}

async function getVideoDurationSeconds(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
      video.removeAttribute("src");
      video.load();
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const resolveIfReady = () => {
      if (settled) return;
      if (Number.isFinite(video.duration) && video.duration > 0) {
        settled = true;
        const duration = video.duration;
        cleanup();
        resolve(duration);
      }
    };

    video.onloadedmetadata = resolveIfReady;
    video.onloadeddata = resolveIfReady;
    video.ondurationchange = resolveIfReady;
    video.onseeked = resolveIfReady;
    video.onerror = () => rejectOnce(new Error("Failed to read video metadata"));

    timeoutId = setTimeout(() => rejectOnce(new Error("Timed out reading video metadata")), 8000);

    video.src = url;
    video.load();
    try {
      video.currentTime = 1e101;
    } catch {
      // ignore seek errors; metadata events can still provide duration
    }
  });
}

async function validateUploadableVideoDuration(file, itemType = "") {
  if (!(file instanceof File)) return { ok: true };
  const normalizedType = String(itemType || file.type || "").toLowerCase();
  const shouldProbe = normalizedType.startsWith("video/") || !normalizedType || fileLooksLikeVideo(file);
  if (!shouldProbe) return { ok: true };

  try {
    const duration = await getVideoDurationSeconds(file);
    const isVideo = Number.isFinite(duration) && duration > 0;
    if (!isVideo) return { ok: true };
    if (duration > MAX_SHARE_FLOW_VIDEO_VALIDATION_SECONDS) {
      return {
        ok: false,
        reason: `That video is too long. Please choose ${MAX_SHARE_FLOW_VIDEO_SECONDS} seconds or less.`,
      };
    }
    return { ok: true };
  } catch {
    if (!normalizedType.startsWith("video/") && !fileLooksLikeVideo(file)) {
      return { ok: true };
    }
    return {
      ok: false,
      reason: `We couldn’t read that video’s length. Please choose ${MAX_SHARE_FLOW_VIDEO_SECONDS}s or less.`,
    };
  }
}

const getDraftStepOrder = () => ["compose", "media"];
const getStepIndexForDraftStep = (draftStep) => {
  const normalizedDraftStep = String(draftStep || "").toLowerCase();
  const normalizedStep = ["business", "share"].includes(normalizedDraftStep)
    ? "media"
    : normalizedDraftStep;
  const order = getDraftStepOrder();
  const index = order.indexOf(normalizedStep);
  return index >= 0 ? index : 0;
};
function parseOptionalDate(value) {
  if (!value) return null;
  const nextDate = value instanceof Date ? value : new Date(value);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
}

function serializeOptionalDate(value) {
  const nextDate = parseOptionalDate(value);
  return nextDate ? nextDate.toISOString() : "";
}

function normalizeSeedType(value) {
  const next = String(value || "").trim().toLowerCase();
  return ["see", "eat", "drink", "do", "shop", "relax"].includes(next) ? next : "";
}

async function buildVideoPreviewBestEffort(file) {
  try {
    return await Promise.race([
      buildVideoSessionPreviewDataUrl(file),
      new Promise((resolve) => {
        setTimeout(() => resolve(""), VIDEO_PREVIEW_BEST_EFFORT_MS);
      }),
    ]);
  } catch {
    return "";
  }
}

async function uploadVideoPosterThumbnail({ sessionPreviewUrl = "", sourceName = "", index = 0 } = {}) {
  const posterFile = buildVideoPosterFileFromPreview(sessionPreviewUrl, sourceName);
  if (!posterFile) return {};

  const posterPath = `timecapsules/thumbnails/${Date.now()}-${index}-${posterFile.name}`;
  const fileRef = storageRef(storage, posterPath);
  await uploadWithRetries({
    storageRef: fileRef,
    file: posterFile,
    metadata: { contentType: posterFile.type || "image/webp" },
    maxRetries: 2,
    stallMs: 15000,
  });
  const posterUrl = await getDownloadURL(fileRef);
  return {
    thumbnailUrl: posterUrl,
    posterUrl,
    posterStoragePath: posterPath,
  };
}

function buildRefreshRecoveryPayload({ wizardData, activeStep, includeTransientLocalMedia = true }) {
  const mediaFiles = (wizardData?.mediaFiles || [])
    .map((item) => {
      const url = String(item?.url || "").trim();
      const isBlobUrl = url.startsWith("blob:");
      const sessionPreviewUrl = String(item?.sessionPreviewUrl || "").trim();
      const thumbnailUrl = String(item?.thumbnailUrl || "").trim();
      const posterUrl = String(item?.posterUrl || "").trim();
      const posterStoragePath = String(item?.posterStoragePath || "").trim();
      const snapshotUrl = isBlobUrl
        ? (includeTransientLocalMedia ? sessionPreviewUrl : "")
        : url;
      if (!snapshotUrl) return null;
      const snapshotType = isBlobUrl ? "image/webp" : (item?.type || "");
      const snapshotFileName = isBlobUrl
        ? (
            String(item?.file?.name || "").trim().replace(/\.[^.]+$/, ".webp")
            || `image_${Date.now()}.webp`
          )
        : "";
      return {
        url: snapshotUrl,
        type: snapshotType,
        source: item?.source || "",
        storagePath: item?.storagePath || "",
        draftOwnedMedia: item?.draftOwnedMedia === true,
        fileName: snapshotFileName,
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(posterUrl ? { posterUrl } : {}),
        ...(posterStoragePath ? { posterStoragePath } : {}),
        ...(sessionPreviewUrl && !thumbnailUrl ? { sessionPreviewUrl } : {}),
      };
    })
    .filter(Boolean);

  return {
    savedAt: Date.now(),
    activeStep,
    wizardData: {
      lat: Number.isFinite(wizardData?.lat) ? wizardData.lat : 0,
      lng: Number.isFinite(wizardData?.lng) ? wizardData.lng : 0,
      address: wizardData?.address || "",
      placeName: wizardData?.placeName || "",
      placeId: wizardData?.placeId || "",
      subject: wizardData?.subject || "",
      message: wizardData?.message || "",
      seedType: normalizeSeedType(wizardData?.seedType),
      mediaFiles,
      audience: wizardData?.audience || "share",
      recipients: Array.isArray(wizardData?.recipients) ? wizardData.recipients : [],
      phoneNumbers: Array.isArray(wizardData?.phoneNumbers) ? wizardData.phoneNumbers : [],
      revealDate: serializeOptionalDate(wizardData?.revealDate),
      endDate: serializeOptionalDate(wizardData?.endDate),
      callToActionText: wizardData?.callToActionText || "",
      callToActionUrl: wizardData?.callToActionUrl || "",
      hashtags: Array.isArray(wizardData?.hashtags) ? wizardData.hashtags : [],
      isBusinessCapsule: Boolean(wizardData?.isBusinessCapsule),
      hasChosenCapsuleType: Boolean(wizardData?.hasChosenCapsuleType),
      draftId: wizardData?.draftId || "",
      draftStep: wizardData?.draftStep || "",
    },
  };
}

function readRefreshRecoveryPayload(storageKey) {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt || 0);
    if (!savedAt || Date.now() - savedAt > REFRESH_RECOVERY_TTL_MS) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

const TimeCapsuleWizard = () => {
  const { privateCapsuleShareFlowEnabled } = useAppConfigFlags();
  const theme = useTheme();
  const isCompactStepper = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const capsule = location.state?.capsule;
  const isForwardedCapsule =
    location.state?.forwardMode === "editable" ||
    Boolean(capsule?.forwardSourceId || capsule?.forwardedFromId);
  const shouldForceFreshSession = shouldUseFreshTimeCapsuleWizardSession({
    forwardMode: location.state?.forwardMode,
    forceFreshSession: location.state?.forceFreshSession,
    capsule,
  });
  const [searchParams] = useSearchParams();
  const initialCoords =
    getUsableLatLng(searchParams.get("lat"), searchParams.get("lng")) ||
    getUsableLatLng(capsule?.location?.lat, capsule?.location?.lng) ||
    { lat: 0, lng: 0 };
  const initialLat = initialCoords.lat;
  const initialLng = initialCoords.lng;
  const initialAddress = searchParams.get("address") || "";
  const initialPlace = searchParams.get("place") || capsule?.location?.placeName || "";
  const initialPlaceId = searchParams.get("placeId") || capsule?.location?.placeId || "";
  const refreshRecoveryKey = useMemo(
    () => `timecapsule-refresh:${location.pathname}`,
    [location.pathname]
  );
  const refreshRecovery = useMemo(
    () => readRefreshRecoveryPayload(refreshRecoveryKey),
    [refreshRecoveryKey]
  );
  const isDraftCapsule =
    capsule?.lifecycleStatus === "draft" ||
    capsule?.status === "draft" ||
    capsule?.isDraft === true;
  const hasRecoveryDraftMismatch =
    Boolean(isDraftCapsule && capsule?.id) &&
    Boolean(refreshRecovery?.wizardData?.draftId) &&
    String(refreshRecovery?.wizardData?.draftId || "").trim() !== String(capsule?.id || "").trim();
  const recoveredWizardData = shouldForceFreshSession || hasRecoveryDraftMismatch
    ? null
    : (refreshRecovery?.wizardData || null);

  const recoveredStep = !shouldForceFreshSession && !hasRecoveryDraftMismatch && Number.isInteger(refreshRecovery?.activeStep)
    ? refreshRecovery.activeStep
    : null;
  const initialStep =
    recoveredStep !== null
      ? Math.max(0, Math.min(recoveredStep, (privateCapsuleShareFlowEnabled ? stepsShareFlow : stepsLegacy).length - 1))
      : (
          privateCapsuleShareFlowEnabled && isDraftCapsule
            ? getStepIndexForDraftStep(capsule?.draftStep)
            : 0
        );
  const [activeStep, setActiveStep] = useState(initialStep);
  const [stepDirection, setStepDirection] = useState(1);
  const [, setIsStepNavigationLocked] = useState(false);
  const [isUploadingMediaForShare, setIsUploadingMediaForShare] = useState(false);
  const [mediaUploadProgress, setMediaUploadProgress] = useState(0);
  const [mediaUploadError, setMediaUploadError] = useState("");
  const [invalidMediaByClientId, setInvalidMediaByClientId] = useState({});
  const prefillAddress = capsule?.location?.address || initialAddress;
  const placeOrAddress = initialPlace || initialAddress || prefillAddress;
  const prefillSubject =
    capsule?.messageTitle ||
    capsule?.subject ||
    placeOrAddress ||
    capsule?.location?.address ||
    "";
  const prefillMessage =
    capsule?.messageBody ||
    capsule?.message ||
    (placeOrAddress
      ? initialPlace && initialAddress && initialPlace !== initialAddress
        ? `${FRIEND_CAPSULE_TITLE_PREFIX} ${initialPlace} (${initialAddress})`
        : `${FRIEND_CAPSULE_TITLE_PREFIX} ${placeOrAddress}`
      : "");
  const prefillMedia =
    Array.isArray(capsule?.media) && capsule.media.length
      ? capsule.media.map((m) =>
          isForwardedCapsule
            ? mapForwardedMediaToWizardItem(m)
            : mapStoredMediaToWizardItem(m)
        )
      : [];
  const recoveredMedia =
    Array.isArray(recoveredWizardData?.mediaFiles) && recoveredWizardData.mediaFiles.length
      ? recoveredWizardData.mediaFiles.map((m) => mapStoredMediaToWizardItem(m))
      : [];
  const prefillRevealDate = capsule?.revealDate?.toDate
    ? capsule.revealDate.toDate()
    : (capsule?.revealDate ? new Date(capsule.revealDate) : null);
  const prefillEndDate = capsule?.endDate?.toDate
    ? capsule.endDate.toDate()
    : (capsule?.endDate ? new Date(capsule.endDate) : null);

  const [wizardData, setWizardData] = useState({
    lat: recoveredWizardData?.lat ?? initialLat,
    lng: recoveredWizardData?.lng ?? initialLng,
    address: recoveredWizardData?.address ?? prefillAddress,
    placeName: recoveredWizardData?.placeName ?? capsule?.location?.placeName ?? initialPlace,
    placeId: recoveredWizardData?.placeId ?? initialPlaceId,
    subject: recoveredWizardData?.subject ?? prefillSubject,
    message: recoveredWizardData?.message ?? prefillMessage,
    seedType: normalizeSeedType(recoveredWizardData?.seedType ?? capsule?.seedType ?? ""),
    mediaFile: recoveredMedia[0] || prefillMedia[0] || null,
    mediaFiles: recoveredMedia.length ? recoveredMedia : prefillMedia,
    audience: recoveredWizardData?.audience ?? capsule?.audience ?? "share",
    recipients: Array.isArray(recoveredWizardData?.recipients)
      ? recoveredWizardData.recipients
      : (capsule?.recipients || []),
    phoneNumbers: Array.isArray(recoveredWizardData?.phoneNumbers)
      ? recoveredWizardData.phoneNumbers
      : (capsule?.recipientPhoneNumbers || []),
    dropCount: 10,
    dropUnit: "weeks",
    revealDate: parseOptionalDate(recoveredWizardData?.revealDate) || prefillRevealDate,
    endDate: parseOptionalDate(recoveredWizardData?.endDate) || prefillEndDate,
    callToActionText: recoveredWizardData?.callToActionText ?? capsule?.callToActionText ?? "",
    callToActionUrl: recoveredWizardData?.callToActionUrl ?? capsule?.callToActionUrl ?? "",
    sourceCapsuleId: recoveredWizardData?.sourceCapsuleId ?? capsule?.sourceCapsuleId ?? capsule?.forwardSourceId ?? capsule?.forwardedFromId ?? "",
    forwardSourceId: recoveredWizardData?.forwardSourceId ?? capsule?.forwardSourceId ?? capsule?.sourceCapsuleId ?? capsule?.forwardedFromId ?? "",
    forwardedFromType: recoveredWizardData?.forwardedFromType ?? capsule?.forwardedFromType ?? "",
    forwardSourceTitle: recoveredWizardData?.forwardSourceTitle ?? capsule?.forwardSourceTitle ?? "",
    forwardSourceAvatarUrl: recoveredWizardData?.forwardSourceAvatarUrl ?? capsule?.forwardSourceAvatarUrl ?? "",
    sourceShareToken: recoveredWizardData?.sourceShareToken ?? capsule?.sourceShareToken ?? "",
    hashtags: Array.isArray(recoveredWizardData?.hashtags)
      ? recoveredWizardData.hashtags
      : (Array.isArray(capsule?.hashtags) ? capsule.hashtags : []),
    isBusinessCapsule: Boolean(recoveredWizardData?.isBusinessCapsule),
    hasChosenCapsuleType: Boolean(recoveredWizardData?.hasChosenCapsuleType),
    draftId: isForwardedCapsule
      ? ""
      : (recoveredWizardData?.draftId ?? (isDraftCapsule ? capsule?.id || "" : "")),
    draftStep: recoveredWizardData?.draftStep || getDraftStepOrder()[initialStep] || "compose",
  });
  const shareFlowSteps = useMemo(
    () => (privateCapsuleShareFlowEnabled ? stepsShareFlow : stepsLegacy),
    [privateCapsuleShareFlowEnabled]
  );
  const autosaveEnabled = privateCapsuleShareFlowEnabled && (isDraftCapsule || Boolean(wizardData.draftId));
  const autosaveInFlightRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const lastAutosaveSigRef = useRef("");
  const initialDraftCreateStartedRef = useRef(false);
  const initialDraftCreateAttemptsRef = useRef(0);
  const initialDraftRetryTimerRef = useRef(null);
  const initialDraftSnackbarTimerRef = useRef(null);
  const mediaUploadPromiseRef = useRef(null);
  const isMountedRef = useRef(true);
  const importGooglePlacePhotoToStorage = useMemo(
    () => httpsCallable(firebaseFunctions, "importGooglePlacePhotoToStorage"),
    []
  );
  const copyForwardedTimeCapsuleMediaToStorage = useMemo(
    () => httpsCallable(firebaseFunctions, "copyForwardedTimeCapsuleMediaToStorage"),
    []
  );
  const autoSaveDraftRef = useRef(null);
  const autosaveSignatureRef = useRef("");
  const contentScrollRef = useRef(null);
  const [initialDraftRetryTick, setInitialDraftRetryTick] = useState(0);
  const [draftCreatedSnackbarOpen, setDraftCreatedSnackbarOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [isDiscardingDraft, setIsDiscardingDraft] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [hasCompletedBury, setHasCompletedBury] = useState(false);
  const [hasStartedFinalSubmission, setHasStartedFinalSubmission] = useState(false);
  const [buryDialogRequestId, setBuryDialogRequestId] = useState(0);

  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const previousHtmlOverflow = htmlEl.style.overflow;
    const previousHtmlOverscroll = htmlEl.style.overscrollBehavior;
    const previousBodyOverflow = bodyEl.style.overflow;
    const previousBodyOverscroll = bodyEl.style.overscrollBehavior;
    const previousBodyPosition = bodyEl.style.position;
    const previousBodyTop = bodyEl.style.top;
    const previousBodyWidth = bodyEl.style.width;
    const scrollY = window.scrollY;

    htmlEl.style.overflow = "hidden";
    htmlEl.style.overscrollBehavior = "none";
    bodyEl.style.overflow = "hidden";
    bodyEl.style.overscrollBehavior = "none";
    bodyEl.style.position = "fixed";
    bodyEl.style.top = `-${scrollY}px`;
    bodyEl.style.width = "100%";

    return () => {
      htmlEl.style.overflow = previousHtmlOverflow;
      htmlEl.style.overscrollBehavior = previousHtmlOverscroll;
      bodyEl.style.overflow = previousBodyOverflow;
      bodyEl.style.overscrollBehavior = previousBodyOverscroll;
      bodyEl.style.position = previousBodyPosition;
      bodyEl.style.top = previousBodyTop;
      bodyEl.style.width = previousBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const {
    send: autoSaveDraft,
  } = usePrivateTimeCapsuleSend({ wizardData });

  const autosaveSignature = useMemo(
    () =>
      JSON.stringify({
        lat: wizardData.lat,
        lng: wizardData.lng,
        address: wizardData.address,
        placeName: wizardData.placeName,
        placeId: wizardData.placeId || "",
        subject: wizardData.subject,
        message: wizardData.message,
        seedType: normalizeSeedType(wizardData.seedType),
        audience: wizardData.audience,
        revealDate: serializeOptionalDate(wizardData.revealDate),
        endDate: serializeOptionalDate(wizardData.endDate),
        callToActionText: wizardData.callToActionText || "",
        callToActionUrl: wizardData.callToActionUrl || "",
        hashtags: Array.isArray(wizardData.hashtags) ? wizardData.hashtags : [],
        isBusinessCapsule: Boolean(wizardData.isBusinessCapsule),
        draftStep: wizardData.draftStep || "",
        mediaFiles: (wizardData.mediaFiles || []).map((m) => ({
          url: m?.url || "",
          type: m?.type || "",
          source: m?.source || "",
          storagePath: m?.storagePath || "",
          draftOwnedMedia: m?.draftOwnedMedia === true,
          thumbnailUrl: m?.thumbnailUrl || "",
          posterUrl: m?.posterUrl || "",
          posterStoragePath: m?.posterStoragePath || "",
          sessionPreviewUrl: m?.thumbnailUrl ? "" : (m?.sessionPreviewUrl || ""),
          hasFile: Boolean(m?.file),
          fileName: m?.file?.name || "",
          size: m?.file?.size || 0,
          lastModified: m?.file?.lastModified || 0,
        })),
      }),
    [wizardData]
  );

  useEffect(() => {
    autoSaveDraftRef.current = autoSaveDraft;
  }, [autoSaveDraft]);

  useEffect(() => {
    autosaveSignatureRef.current = autosaveSignature;
  }, [autosaveSignature]);

  useEffect(() => {
    if (hasCompletedBury) {
      try {
        sessionStorage.removeItem(refreshRecoveryKey);
      } catch {
        // ignore storage failures
      }
      return;
    }

    try {
      const fullPayload = buildRefreshRecoveryPayload({
        wizardData,
        activeStep,
        includeTransientLocalMedia: true,
      });
      sessionStorage.setItem(
        refreshRecoveryKey,
        JSON.stringify(fullPayload)
      );
    } catch {
      try {
        const fallbackPayload = buildRefreshRecoveryPayload({
          wizardData,
          activeStep,
          includeTransientLocalMedia: false,
        });
        sessionStorage.setItem(
          refreshRecoveryKey,
          JSON.stringify(fallbackPayload)
        );
      } catch {
        // ignore storage failures
      }
    }
  }, [activeStep, hasCompletedBury, refreshRecoveryKey, wizardData]);

  useEffect(() => {
    if (!privateCapsuleShareFlowEnabled) return;

    setWizardData((prev) => {
      if (
        !prev.isBusinessCapsule &&
        !(prev.callToActionText || "").trim() &&
        !(prev.callToActionUrl || "").trim() &&
        !(Array.isArray(prev.hashtags) && prev.hashtags.length) &&
        !prev.hasChosenCapsuleType
      ) {
        return prev;
      }

      return {
        ...prev,
        isBusinessCapsule: false,
        hasChosenCapsuleType: false,
        callToActionText: "",
        callToActionUrl: "",
        hashtags: [],
      };
    });
  }, [privateCapsuleShareFlowEnabled]);

  useEffect(() => {
    if (recoveredStep !== null) return;
    if (!privateCapsuleShareFlowEnabled || !isDraftCapsule) return;
    const nextStep = getStepIndexForDraftStep(capsule?.draftStep);
    setActiveStep((prev) => (prev === nextStep ? prev : nextStep));
  }, [capsule?.draftStep, isDraftCapsule, privateCapsuleShareFlowEnabled, recoveredStep]);

  useEffect(() => {
    if (!hasRecoveryDraftMismatch) return;
    try {
      sessionStorage.removeItem(refreshRecoveryKey);
    } catch {
      // ignore storage failures
    }
  }, [hasRecoveryDraftMismatch, refreshRecoveryKey]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (initialDraftRetryTimerRef.current) {
        clearTimeout(initialDraftRetryTimerRef.current);
        initialDraftRetryTimerRef.current = null;
      }
      if (initialDraftSnackbarTimerRef.current) {
        clearTimeout(initialDraftSnackbarTimerRef.current);
        initialDraftSnackbarTimerRef.current = null;
      }
    };
  }, []);

  const initialDraftLockKey = useMemo(
    () => `timecapsule-initial-draft:${location.pathname}`,
    [location.pathname]
  );

  useEffect(() => {
    if (!shouldForceFreshSession) return;

    try {
      sessionStorage.removeItem(refreshRecoveryKey);
      sessionStorage.removeItem(initialDraftLockKey);
    } catch {
      // ignore storage failures
    }

    navigate(location.pathname + location.search, {
      replace: true,
      state: {
        ...(location.state || {}),
        forceFreshSession: false,
      },
    });
  }, [
    initialDraftLockKey,
    location.pathname,
    location.search,
    location.state,
    navigate,
    refreshRecoveryKey,
    shouldForceFreshSession,
  ]);

  useEffect(() => {
    if (activeStep !== 0) return;
    if (isDraftCapsule) return;
    if (wizardData.draftId) return;
    if (initialDraftCreateStartedRef.current) return;

    let lockedDraftId = "";
    try {
      lockedDraftId = String(sessionStorage.getItem(initialDraftLockKey) || "").trim();
    } catch {
      lockedDraftId = "";
    }

    if (lockedDraftId) {
      lastAutosaveSigRef.current = autosaveSignatureRef.current;
      setWizardData((prev) => ({ ...prev, draftId: prev.draftId || lockedDraftId }));
      if (initialDraftSnackbarTimerRef.current) clearTimeout(initialDraftSnackbarTimerRef.current);
      initialDraftSnackbarTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) setDraftCreatedSnackbarOpen(true);
      }, 350);
      return;
    }

    initialDraftCreateStartedRef.current = true;

    (async () => {
      const sendDraft = autoSaveDraftRef.current;
      const result = typeof sendDraft === "function"
        ? await sendDraft({ status: "draft", uploadLocalMedia: false })
        : null;
      if (!isMountedRef.current) return;

      if (!result?.capsuleId) {
        initialDraftCreateStartedRef.current = false;
        initialDraftCreateAttemptsRef.current += 1;
        if (initialDraftCreateAttemptsRef.current <= INITIAL_DRAFT_CREATE_MAX_ATTEMPTS) {
          if (initialDraftRetryTimerRef.current) clearTimeout(initialDraftRetryTimerRef.current);
          initialDraftRetryTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) setInitialDraftRetryTick((v) => v + 1);
          }, 1500);
        }
        return;
      }

      initialDraftCreateAttemptsRef.current = 0;
      const createdDraftId = String(result.capsuleId || "").trim();
      if (createdDraftId) {
        try {
          sessionStorage.setItem(initialDraftLockKey, createdDraftId);
        } catch {
          // ignore storage failures
        }
      }
      lastAutosaveSigRef.current = autosaveSignatureRef.current;
      setWizardData((prev) => ({
        ...prev,
        draftId: createdDraftId || prev.draftId || "",
      }));
      if (initialDraftSnackbarTimerRef.current) {
        clearTimeout(initialDraftSnackbarTimerRef.current);
      }
      initialDraftSnackbarTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) setDraftCreatedSnackbarOpen(true);
      }, 350);
    })();
  }, [activeStep, initialDraftLockKey, initialDraftRetryTick, isDraftCapsule, wizardData.draftId]);

  useEffect(() => {
    if (!autosaveEnabled) return;
    if (activeStep >= shareFlowSteps.length - 1) return;
    if (autosaveInFlightRef.current) return;
    if (!wizardData.subject?.trim() && !wizardData.message?.trim() && !(wizardData.mediaFiles || []).length) return;
    if (autosaveSignature === lastAutosaveSigRef.current) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      if (autosaveInFlightRef.current) return;
      autosaveInFlightRef.current = true;
      const sig = autosaveSignature;
      try {
        const result = await autoSaveDraft({ status: "draft", uploadLocalMedia: false });
        if (!result) return;
        lastAutosaveSigRef.current = sig;
        setWizardData((prev) => ({
          ...prev,
          draftId: result.capsuleId || prev.draftId || "",
        }));
      } finally {
        autosaveInFlightRef.current = false;
      }
    }, 1500);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [activeStep, autosaveEnabled, autosaveSignature, autoSaveDraft, shareFlowSteps.length, wizardData.mediaFiles, wizardData.message, wizardData.subject]);

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [activeStep]);

  useEffect(() => {
    const currentIds = new Set(
      (Array.isArray(wizardData?.mediaFiles) ? wizardData.mediaFiles : [])
        .map((item) => String(item?.clientId || "").trim())
        .filter(Boolean)
    );
    setInvalidMediaByClientId((prev) => {
      const entries = Object.entries(prev).filter(([clientId]) => currentIds.has(clientId));
      if (entries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(entries);
    });
  }, [wizardData?.mediaFiles]);

  useEffect(() => {
    if (!privateCapsuleShareFlowEnabled) return;
    setWizardData((prev) => ({
      ...prev,
      draftStep: getDraftStepOrder()[activeStep] || "compose",
    }));
  }, [activeStep, privateCapsuleShareFlowEnabled]);

  const uploadPendingMediaForShare = useCallback(async () => {
    if (mediaUploadPromiseRef.current) {
      return mediaUploadPromiseRef.current;
    }

    const mediaItems = Array.isArray(wizardData.mediaFiles) ? wizardData.mediaFiles : [];
    const pending = mediaItems.filter((m) => shouldUploadBeforeBury(m));
    if (!pending.length) return mediaItems;

    const run = (async () => {
      setMediaUploadError("");
      setMediaUploadProgress(0);

      try {
        try {
          await checkNetworkReachability();
        } catch (err) {
          setMediaUploadError(
            err?.code === NETWORK_OFFLINE_CODE
              ? "You're offline. Connect to the internet and try again."
              : "We can't reach the network right now. Check your connection and try again."
          );
          return null;
        }

        setIsUploadingMediaForShare(true);
        setIsStepNavigationLocked(true);

        let uploadedCount = 0;
        const updatedItems = [...mediaItems];

        for (let i = 0; i < mediaItems.length; i += 1) {
          const item = mediaItems[i];
          if (!shouldUploadBeforeBury(item)) continue;
          if (isGooglePlaceImportUrlItem(item)) {
            const completedSoFar = uploadedCount;
            setMediaUploadProgress(
              Math.round(((completedSoFar + 0.1) / pending.length) * 100)
            );
            const imported = await importGooglePlacePhotoToStorage({
              photoUrl: String(item?.url || "").trim(),
              contentType: String(item?.type || "image/jpeg"),
            });
            const importedData = imported?.data || {};
            const importedUrl = String(importedData?.downloadURL || "").trim();
            const importedPath = String(importedData?.storagePath || "").trim();
            if (!importedUrl || !importedPath) {
              throw new Error("Cloud function did not return uploaded place photo details.");
            }
            uploadedCount += 1;
            updatedItems[i] = {
              ...item,
              file: null,
              url: importedUrl,
              type: item?.type || String(importedData?.contentType || "image/jpeg"),
              source: item?.source || "place",
              storagePath: importedPath,
              draftOwnedMedia: true,
            };
            setMediaUploadProgress(
              Math.round((uploadedCount / pending.length) * 100)
            );
            continue;
          }
          if (isForwardedSourceMediaItem(item)) {
            const completedSoFar = uploadedCount;
            setMediaUploadProgress(
              Math.round(((completedSoFar + 0.1) / pending.length) * 100)
            );
            const copied = await copyForwardedTimeCapsuleMediaToStorage({
              sourceCapsuleId: String(
                wizardData.sourceCapsuleId ||
                  wizardData.forwardSourceId ||
                  capsule?.sourceCapsuleId ||
                  capsule?.forwardSourceId ||
                  capsule?.forwardedFromId ||
                  ""
              ).trim(),
              sourceStoragePath: String(item?.sourceStoragePath || item?.storagePath || "").trim(),
              sourceUrl: String(item?.url || "").trim(),
              contentType: String(item?.type || "").trim(),
              sourceShareToken: String(wizardData.sourceShareToken || capsule?.sourceShareToken || "").trim(),
            });
            const copiedData = copied?.data || {};
            const copiedUrl = String(copiedData?.downloadURL || "").trim();
            const copiedPath = String(copiedData?.storagePath || "").trim();
            const copiedType = String(
              item?.type || copiedData?.contentType || "application/octet-stream"
            ).trim();
            if (!copiedUrl || !copiedPath) {
              throw new Error("Cloud function did not return copied media details.");
            }

            let videoPosterMetadata = {};
            const sessionPreviewUrl = String(item?.sessionPreviewUrl || "").trim();
            const isVideo = copiedType.toLowerCase().startsWith("video/");
            if (sessionPreviewUrl && isVideo) {
              try {
                videoPosterMetadata = await uploadVideoPosterThumbnail({
                  sessionPreviewUrl,
                  sourceName: item?.name || `forwarded-video-${i}.mp4`,
                  index: i,
                });
              } catch (posterErr) {
                console.warn("Unable to upload forwarded video thumbnail before share:", posterErr);
              }
            }

            uploadedCount += 1;
            updatedItems[i] = {
              ...item,
              file: null,
              url: copiedUrl,
              type: copiedType,
              storagePath: copiedPath,
              sourceStoragePath: "",
              draftOwnedMedia: true,
              forwardedSourceMedia: false,
              posterStoragePath: "",
              ...videoPosterMetadata,
              ...(sessionPreviewUrl ? { sessionPreviewUrl } : {}),
            };
            setMediaUploadProgress(
              Math.round((uploadedCount / pending.length) * 100)
            );
            continue;
          }
          const preUploadValidation = await validateUploadableVideoDuration(item?.file, item?.type);
          if (!preUploadValidation.ok) {
            const reason = preUploadValidation.reason || "Video is too long.";
            const clientId = String(item?.clientId || "").trim();
            if (clientId) {
              setInvalidMediaByClientId((prev) => ({ ...prev, [clientId]: reason }));
            }
            setMediaUploadError(reason);
            return null;
          }

          const file = await buildUploadableMediaFile(item, i);
          if (!file) continue;
          let sessionPreviewUrl = String(item?.sessionPreviewUrl || "").trim();
          const isVideo = String(item?.type || file?.type || "").toLowerCase().startsWith("video/");
          if (!sessionPreviewUrl && isVideo && file instanceof File) {
            sessionPreviewUrl = await buildVideoPreviewBestEffort(file);
          }
          let videoPosterMetadata = {};
          if (sessionPreviewUrl && isVideo) {
            try {
              videoPosterMetadata = await uploadVideoPosterThumbnail({
                sessionPreviewUrl,
                sourceName: file?.name || item?.name || `video-${i}.mp4`,
                index: i,
              });
            } catch (posterErr) {
              console.warn("Unable to upload video thumbnail before share:", posterErr);
            }
          }
          const path = `timecapsules/${Date.now()}-${i}-${file.name}`;
          const fileRef = storageRef(storage, path);
          const metadata = file?.type ? { contentType: file.type } : undefined;
          const completedSoFar = uploadedCount;
          await uploadWithRetries({
            storageRef: fileRef,
            file,
            metadata,
            maxRetries: 2,
            firstProgressStallMs: 12000,
            stallMs: 25000,
            onProgress: (snapshot) => {
              const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              const overall = Math.round(((completedSoFar + pct / 100) / pending.length) * 100);
              setMediaUploadProgress(overall);
            },
          });

          const downloadURL = await getDownloadURL(fileRef);
          uploadedCount += 1;
          updatedItems[i] = {
            ...item,
            file: null,
            url: downloadURL,
            type: item?.type || file?.type || "image/jpeg",
            source: item?.source || "",
            storagePath: path,
            draftOwnedMedia: true,
            ...videoPosterMetadata,
            ...(sessionPreviewUrl ? { sessionPreviewUrl } : {}),
          };
        }

        setWizardData((prev) => ({ ...prev, mediaFiles: updatedItems }));
        setMediaUploadProgress(100);
        return updatedItems;
      } catch (err) {
        console.error(
          "Failed to upload media before share step:",
          err,
          pending.map(getMediaItemDiagnostics)
        );
        const retryable =
          err?.code === "storage/retry-limit-exceeded" ||
          err?.code === "storage/canceled" ||
          err?.code === "storage/network-request-failed";
        setMediaUploadError(
          retryable
            ? "Upload interrupted by network conditions. Please try again."
            : "Upload failed. Please try again."
        );
        return null;
      } finally {
        setIsUploadingMediaForShare(false);
        setIsStepNavigationLocked(false);
      }
    })();

    mediaUploadPromiseRef.current = run;
    try {
      return await run;
    } finally {
      mediaUploadPromiseRef.current = null;
    }
  }, [capsule, copyForwardedTimeCapsuleMediaToStorage, importGooglePlacePhotoToStorage, wizardData]);

  const hasPendingLocalMedia = useCallback(() => {
    const mediaItems = Array.isArray(wizardData.mediaFiles) ? wizardData.mediaFiles : [];
    return mediaItems.some((item) => shouldUploadBeforeBury(item));
  }, [wizardData.mediaFiles]);

  const handleNext = async () => {
    if (
      privateCapsuleShareFlowEnabled &&
      activeStep === 1 &&
      hasPendingLocalMedia()
    ) {
      const uploadedItems = await uploadPendingMediaForShare();
      if (!uploadedItems) {
        return;
      }
    }

    if (privateCapsuleShareFlowEnabled && activeStep >= shareFlowSteps.length - 1) {
      setBuryDialogRequestId((prev) => prev + 1);
      return;
    }

    if (activeStep < shareFlowSteps.length - 1) {
      setStepDirection(1);
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = async () => {
    if (activeStep > 0) {
      setStepDirection(-1);
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleDeleteUploadedMedia = useCallback(async (item) => {
    if (item?.draftOwnedMedia !== true) return;
    const storagePath = String(item?.storagePath || "").trim();
    if (!storagePath) return;
    try {
      await deleteObject(storageRef(storage, storagePath));
    } catch (err) {
      console.warn("Failed to delete removed media from storage:", err);
    }
  }, []);

  const exitToHome = useCallback(({ showDraftSaved = false, discardedDraftId = "" } = {}) => {
    const coords = getUsableLatLng(wizardData?.lat, wizardData?.lng);
    const normalizedDiscardedDraftId = String(discardedDraftId || "").trim();
    const state = coords
      ? {
          center: coords,
          selectedPlaceName: wizardData?.placeName || "",
          selectedPlaceAddress: wizardData?.address || "",
          selectedPlaceId: wizardData?.placeId || "",
          showDraftSaved,
          discardedDraftId: normalizedDiscardedDraftId,
        }
      : {
          selectedPlaceName: wizardData?.placeName || "",
          selectedPlaceAddress: wizardData?.address || "",
          selectedPlaceId: wizardData?.placeId || "",
          showDraftSaved,
          discardedDraftId: normalizedDiscardedDraftId,
        };
    navigate("/", { state });
  }, [navigate, wizardData?.address, wizardData?.lat, wizardData?.lng, wizardData?.placeId, wizardData?.placeName]);

  const handleSaveAndExit = useCallback(async () => {
    if (isSavingDraft) return;
    setIsSavingDraft(true);
    let mediaFilesForExit = Array.isArray(wizardData.mediaFiles) ? wizardData.mediaFiles : [];

    try {
      if (privateCapsuleShareFlowEnabled && hasPendingLocalMedia()) {
        const uploadedItems = await uploadPendingMediaForShare();
        if (!uploadedItems) {
          return;
        }
        mediaFilesForExit = uploadedItems;
      }

      const shouldFlushDraft =
        privateCapsuleShareFlowEnabled &&
        !hasCompletedBury &&
        !autosaveInFlightRef.current &&
        autosaveSignatureRef.current !== lastAutosaveSigRef.current;

      if (shouldFlushDraft) {
        try {
          autosaveInFlightRef.current = true;
          const result = await autoSaveDraftRef.current?.({
            status: "draft",
            uploadLocalMedia: false,
            mediaFilesOverride: mediaFilesForExit,
          });
          if (result) {
            lastAutosaveSigRef.current = autosaveSignatureRef.current;
            setWizardData((prev) => ({
              ...prev,
              draftId: result.capsuleId || prev.draftId || "",
            }));
          }
        } catch (err) {
          console.warn("Failed to flush draft before exit:", err);
        } finally {
          autosaveInFlightRef.current = false;
        }
      }

      try {
        sessionStorage.removeItem(refreshRecoveryKey);
        sessionStorage.removeItem(initialDraftLockKey);
      } catch {
        // ignore storage failures
      }
      exitToHome({ showDraftSaved: true });
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    exitToHome,
    hasCompletedBury,
    hasPendingLocalMedia,
    initialDraftLockKey,
    isSavingDraft,
    privateCapsuleShareFlowEnabled,
    refreshRecoveryKey,
    uploadPendingMediaForShare,
    wizardData.mediaFiles,
  ]);

  const handleBuryComplete = useCallback(() => {
    setHasCompletedBury(true);
    try {
      sessionStorage.removeItem(initialDraftLockKey);
      sessionStorage.removeItem(refreshRecoveryKey);
    } catch {
      // ignore storage failures
    }
  }, [initialDraftLockKey, refreshRecoveryKey]);

  useEffect(() => {
    const handleTopAppBarBack = (event) => {
      if (event?.detail) {
        event.detail.handled = true;
      }
      handleSaveAndExit();
    };

    window.addEventListener("inssa:timecapsule-back", handleTopAppBarBack);
    return () => {
      window.removeEventListener("inssa:timecapsule-back", handleTopAppBarBack);
    };
  }, [handleSaveAndExit]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("inssa:timecapsule-back-visibility", {
        detail: { hidden: hasCompletedBury },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("inssa:timecapsule-back-visibility", {
          detail: { hidden: false },
        })
      );
    };
  }, [hasCompletedBury]);

  const handleDiscardDraft = useCallback(async () => {
    const draftId = String(wizardData?.draftId || "").trim();
    if (!draftId || isDiscardingDraft) {
      setDiscardDialogOpen(false);
      return;
    }

    setIsDiscardingDraft(true);
    try {
      const mediaItems = Array.isArray(wizardData?.mediaFiles) ? wizardData.mediaFiles : [];
      const storagePaths = Array.from(
        new Set(
          mediaItems
            .filter((item) => item?.draftOwnedMedia === true)
            .map((item) => String(item?.storagePath || "").trim())
            .filter(Boolean)
        )
      );

      await Promise.all(
        storagePaths.map(async (path) => {
          try {
            await deleteObject(storageRef(storage, path));
          } catch (err) {
            console.warn("Failed to delete draft media from storage:", err);
          }
        })
      );

      await deleteDoc(doc(db, "timeCapsules", draftId));
      try {
        sessionStorage.removeItem(initialDraftLockKey);
        sessionStorage.removeItem(refreshRecoveryKey);
      } catch {
        // ignore storage failures
      }
      setDiscardDialogOpen(false);
      exitToHome({ showDraftSaved: false, discardedDraftId: draftId });
    } catch (err) {
      console.error("Failed to discard draft:", err);
      setDiscardDialogOpen(false);
    } finally {
      setIsDiscardingDraft(false);
    }
  }, [exitToHome, initialDraftLockKey, isDiscardingDraft, refreshRecoveryKey, wizardData?.draftId, wizardData?.mediaFiles]);

  // Simple render function instead of useMemo
  const renderStep = () => {
    if (privateCapsuleShareFlowEnabled) {
      switch (activeStep) {
        case 0:
          return (
            <Step1ComposeFrictionless
              wizardData={wizardData}
              setWizardData={setWizardData}
              onNext={handleNext}
              onSaveAndExit={handleSaveAndExit}
              isSavingDraft={isSavingDraft}
            />
          );
        case 1:
          return (
            <>
              {!hasCompletedBury && (
                <Step2MediaFrictionless
                  wizardData={wizardData}
                  setWizardData={setWizardData}
                  onBack={handleBack}
                  onNext={handleNext}
                  onSaveAndExit={handleSaveAndExit}
                  isSavingDraft={isSavingDraft}
                  isUploadingBeforeShare={isUploadingMediaForShare}
                  uploadProgress={mediaUploadProgress}
                  uploadError={mediaUploadError}
                  onClearUploadError={() => setMediaUploadError("")}
                  invalidMediaByClientId={invalidMediaByClientId}
                  onRemoveUploadedMedia={handleDeleteUploadedMedia}
                />
              )}
              <Step3ShareFrictionless
                wizardData={wizardData}
                setWizardData={setWizardData}
                onBack={handleBack}
                onSaveAndExit={handleSaveAndExit}
                isSavingDraft={isSavingDraft}
                onBuryComplete={handleBuryComplete}
                onNavigationLockChange={setIsStepNavigationLocked}
                onSubmissionStateChange={setHasStartedFinalSubmission}
                isBusinessAccount={false}
                isBusinessFlow={false}
                controllerOnly
                openBuryDialogRequest={buryDialogRequestId}
              />
            </>
          );
        default:
          return null;
      }
    }

    switch (activeStep) {
      case 0:
        return (
          <Step1Compose
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onSaveAndExit={handleSaveAndExit}
          />
        );
      case 1:
        return (
          <Step2Recipients
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <Step3Review
            wizardData={wizardData}
            setWizardData={setWizardData}
            onBack={handleBack}
            onBuryComplete={handleBuryComplete}
            onNavigationLockChange={setIsStepNavigationLocked}
          />
        );
      default:
        return null;
    }
  };

  const stepTransition = useMemo(
    () => ({
      duration: 0.24,
      ease: [0.22, 1, 0.36, 1],
    }),
    []
  );

  const stepVariants = useMemo(
    () => ({
      enter: () => ({
        opacity: 0,
      }),
      center: {
        opacity: 1,
      },
      exit: () => ({
        opacity: 0,
      }),
    }),
    []
  );
  const activeStepTitle = useMemo(() => {
    const labels = privateCapsuleShareFlowEnabled ? stepsShareFlow : stepsLegacy;
    const stepLabel = labels[activeStep];
    if (!stepLabel) return "";
    return `Step ${activeStep + 1}: ${stepLabel}`;
  }, [activeStep, privateCapsuleShareFlowEnabled]);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        backgroundColor: "background.default",
        height: "100vh",
        "@supports (height: 100dvh)": {
          height: "100dvh",
        },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          px: { xs: 2, sm: 4 },
          pt: 2,
          pb: 4,
          backgroundColor: "background.default",
          zIndex: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 2.5, mt: 0.25 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {activeStepTitle && (
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontSize: { xs: "1.05rem", sm: "1.15rem" },
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {activeStepTitle}
              </Box>
            )}
          </Box>
          {!hasStartedFinalSubmission && (
            <Button
              variant="outlined"
              onClick={() => setDiscardDialogOpen(true)}
              disabled={!wizardData?.draftId || isDiscardingDraft}
              startIcon={<CloseIcon fontSize="small" />}
              sx={(theme) => ({
                flex: "0 0 auto",
                fontWeight: 700,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "transparent",
                "&:hover": {
                  borderColor: theme.palette.divider,
                  backgroundColor: theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.02)",
                },
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled,
                  borderColor: theme.palette.action.disabledBackground,
                },
              })}
            >
              Discard draft
            </Button>
          )}
        </Box>
        <Stepper
          nonLinear
          activeStep={activeStep}
          sx={(theme) => ({
            mb: 0.8,
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "& .MuiStep-root": {
              minWidth: isCompactStepper ? 44 : 0,
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: theme.colors.green,
            },
            "& .MuiStepIcon-root.Mui-completed .MuiStepIcon-text": {
              fill: theme.colors.white,
            },
            "& .MuiStepIcon-root.Mui-active": {
              color: theme.colors.blue,
            },
            "& .MuiStepIcon-root.Mui-active.Mui-completed": {
              color: theme.colors.green,
            },
            "& .MuiStepLabel-label": {
              color: theme.palette.text.secondary,
              ...(isCompactStepper
                ? {
                    display: "none",
                  }
                : {}),
            },
            "& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed": {
              color: theme.palette.text.primary,
              fontWeight: 700,
            },
            "& .MuiStepConnector-line": {
              borderColor: theme.palette.divider,
            },
          })}
        >
          {shareFlowSteps.map((label, idx) => (
            <Step
              key={label}
              completed={activeStep > idx || (idx === shareFlowSteps.length - 1 && hasCompletedBury)}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box
        ref={contentScrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          overflowX: "hidden",
          overscrollBehaviorY: "contain",
          px: 0,
          pb: 0,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
          <AnimatePresence mode="wait" initial={false} custom={stepDirection}>
            <Box
              key={activeStep}
              component={motion.div}
              custom={stepDirection}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={stepTransition}
              sx={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {renderStep()}
            </Box>
          </AnimatePresence>
        </Box>
      </Box>

      <Snackbar
        open={draftCreatedSnackbarOpen}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: (theme) => (theme.zIndex?.snackbar || 1400) + 2 }}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setDraftCreatedSnackbarOpen(false);
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={(theme) => ({
            width: "100%",
            backgroundColor: theme.palette.mode === "dark" ? theme.colors.green : undefined,
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            "& .MuiAlert-action": { color: "#fff" },
          })}
          onClose={() => setDraftCreatedSnackbarOpen(false)}
        >
          Draft created and saved to Buried drafts.
        </Alert>
      </Snackbar>

      <Dialog
        open={discardDialogOpen}
        onClose={() => {
          if (isDiscardingDraft) return;
          setDiscardDialogOpen(false);
        }}
        fullWidth
      >
        <DialogTitle>Discard draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the draft from Buried drafts and closes the wizard.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDiscardDialogOpen(false)}
            disabled={isDiscardingDraft}
          >
            Keep editing
          </Button>
          <Button
            onClick={handleDiscardDraft}
            disabled={isDiscardingDraft}
            color="error"
            variant="contained"
            sx={{ fontWeight: 700 }}
          >
            {isDiscardingDraft ? "Discarding..." : "Discard draft"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeCapsuleWizard;
